import { Box, Flex, Spinner, Text } from "@chakra-ui/react";
import React, { useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { studentPalette } from "../../constants/studentPalette";
import { studentSurfaces } from "../../constants/studentThemeSurfaces";

const C = {
  primary: studentPalette.primary,
  secondary: studentPalette.secondary,
  border: studentSurfaces.border,
  paper: studentSurfaces.raised,
  muted: studentSurfaces.textMuted,
};

const TYPE_BAR_COLORS = {
  exams: studentPalette.primary,
  quizzes: "#00AA8D",
  hw: studentPalette.crimson,
};

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <Box
      bg="var(--surface-raised)"
      borderWidth="1px"
      borderColor="var(--border-subtle)"
      borderRadius="lg"
      px={3}
      py={2}
      boxShadow="md"
    >
      {label ? (
        <Text fontSize="xs" fontWeight="800" color="var(--text-muted)" mb={1}>
          {label}
        </Text>
      ) : null}
      {payload.map((entry) => (
        <Text key={entry.name} fontSize="sm" fontWeight="600" color="var(--text-primary)">
          {entry.name}: {entry.value}
          {entry.name?.toLowerCase().includes("accuracy") ||
          ["Exams", "Quizzes", "Homework"].includes(entry.name)
            ? "%"
            : ""}
        </Text>
      ))}
    </Box>
  );
}

function ChartCard({ title, children, minH = "260px" }) {
  return (
    <Box
      p={{ base: 4, md: 6 }}
      bg={C.paper}
      borderWidth="1px"
      borderColor={C.border}
      borderRadius="lg"
      w="full"
      minW={0}
    >
      <Text
        fontSize="10px"
        fontWeight="800"
        letterSpacing="0.14em"
        color={C.muted}
        textTransform="uppercase"
        mb={4}
      >
        {title}
      </Text>
      <Box h={minH} w="full" minW={0}>
        {children}
      </Box>
    </Box>
  );
}

const HomeAnalyticsCharts = ({ performanceData, isLoading, sessionsSlot = null }) => {
  const weeklyData = useMemo(() => {
    const source = performanceData?.weekly_accuracy?.length
      ? performanceData.weekly_accuracy
      : performanceData?.monthly_accuracy || [];

    return source.map((item) => {
      const accuracy = Math.min(
        100,
        Math.max(0, parseFloat(String(item.accuracy)) || 0)
      );
      let weekLabel = item.week_label || item.week || item.month;
      if (!item.week_label && (item.week || item.month)) {
        try {
          const raw = item.week || item.month;
          weekLabel = new Date(
            `${raw}${String(raw).length === 7 ? "-01" : ""}`
          ).toLocaleDateString("en-US", {
            month: "short",
            day: String(raw).length > 7 ? "numeric" : undefined,
            year: "2-digit",
          });
        } catch {
          /* keep raw */
        }
      }
      return {
        week: weekLabel,
        accuracy: Number(accuracy.toFixed(1)),
      };
    });
  }, [performanceData?.weekly_accuracy, performanceData?.monthly_accuracy]);

  const chapterGroupedData = useMemo(() => {
    return [...(performanceData?.chapter_accuracy_by_type || [])]
      .map((ch) => {
        const exams = parseFloat(ch?.exams?.accuracy) || 0;
        const quizzes = parseFloat(ch?.quizzes?.accuracy) || 0;
        const hw = parseFloat(ch?.hw?.accuracy) || 0;
        const total =
          (Number(ch?.exams?.total_count) || 0) +
          (Number(ch?.quizzes?.total_count) || 0) +
          (Number(ch?.hw?.total_count) || 0);
        if (total <= 0) return null;
        const name = ch.name || "Chapter";
        return {
          name: name.length > 18 ? `${name.slice(0, 16)}…` : name,
          fullName: name,
          exams,
          quizzes,
          hw,
          total,
        };
      })
      .filter(Boolean)
      .sort((a, b) => b.total - a.total)
      .slice(0, 10);
  }, [performanceData?.chapter_accuracy_by_type]);

  const axisTick = { fill: "var(--text-muted)", fontSize: 11, fontWeight: 600 };
  const hasData = weeklyData.length > 0 || chapterGroupedData.length > 0;

  if (isLoading && !hasData) {
    return (
      <Flex justify="center" py={16}>
        <Spinner size="lg" color={C.primary} thickness="4px" />
      </Flex>
    );
  }

  if (!hasData) {
    return (
      <Box
        p={8}
        bg={C.paper}
        borderWidth="1px"
        borderColor={C.border}
        borderRadius="lg"
        textAlign="center"
      >
        <Text color={C.muted} fontWeight="600">
          Your analytics will appear after you complete practice questions and exams.
        </Text>
      </Box>
    );
  }

  return (
    <Box w="full" minW={0}>
      <Flex direction="column" gap={6} minW={0}>
        <ChartCard title="Weekly accuracy" minH="240px">
          {weeklyData.length === 0 ? (
            <Flex align="center" justify="center" h="full">
              <Text fontSize="sm" color={C.muted} fontWeight="600">
                No weekly data yet.
              </Text>
            </Flex>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={weeklyData} margin={{ top: 4, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-subtle)" vertical={false} />
                <XAxis dataKey="week" tick={axisTick} axisLine={{ stroke: "var(--border-subtle)" }} />
                <YAxis
                  domain={[0, 100]}
                  tick={axisTick}
                  axisLine={{ stroke: "var(--border-subtle)" }}
                  tickFormatter={(v) => `${v}%`}
                />
                <Tooltip content={<ChartTooltip />} />
                <Legend
                  wrapperStyle={{ fontSize: 10, fontWeight: 700, color: "var(--text-muted)" }}
                  iconSize={8}
                />
                <Line
                  type="monotone"
                  dataKey="accuracy"
                  name="Accuracy %"
                  stroke={studentPalette.crimson}
                  strokeWidth={2.5}
                  dot={{ fill: studentPalette.crimson, r: 3 }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="Accuracy by chapter" minH="300px">
          {chapterGroupedData.length === 0 ? (
            <Flex align="center" justify="center" h="full">
              <Text fontSize="sm" color={C.muted} fontWeight="600">
                Chapter accuracy will appear as you practice.
              </Text>
            </Flex>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chapterGroupedData}
                margin={{ top: 8, right: 8, left: 0, bottom: 8 }}
                barCategoryGap="20%"
                barGap={4}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-subtle)" vertical={false} />
                <XAxis dataKey="name" tick={axisTick} axisLine={{ stroke: "var(--border-subtle)" }} />
                <YAxis
                  domain={[0, 100]}
                  tick={axisTick}
                  axisLine={{ stroke: "var(--border-subtle)" }}
                  tickFormatter={(v) => `${v}%`}
                />
                <Tooltip
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const row = payload[0]?.payload;
                    return (
                      <Box
                        bg="var(--surface-raised)"
                        borderWidth="1px"
                        borderColor="var(--border-subtle)"
                        borderRadius="lg"
                        px={3}
                        py={2}
                        boxShadow="md"
                      >
                        <Text fontSize="sm" fontWeight="800" color="var(--text-primary)" mb={1}>
                          {row?.fullName}
                        </Text>
                        {payload.map((entry) => (
                          <Text
                            key={entry.name}
                            fontSize="xs"
                            fontWeight="600"
                            color="var(--text-muted)"
                          >
                            {entry.name}: {entry.value}%
                          </Text>
                        ))}
                      </Box>
                    );
                  }}
                />
                <Legend
                  wrapperStyle={{ fontSize: 11, fontWeight: 700, color: "var(--text-muted)" }}
                />
                <Bar
                  dataKey="exams"
                  name="Exams"
                  fill={TYPE_BAR_COLORS.exams}
                  radius={[4, 4, 0, 0]}
                  maxBarSize={28}
                />
                <Bar
                  dataKey="quizzes"
                  name="Quizzes"
                  fill={TYPE_BAR_COLORS.quizzes}
                  radius={[4, 4, 0, 0]}
                  maxBarSize={28}
                />
                <Bar
                  dataKey="hw"
                  name="Homework"
                  fill={TYPE_BAR_COLORS.hw}
                  radius={[4, 4, 0, 0]}
                  maxBarSize={28}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        {sessionsSlot}
      </Flex>
    </Box>
  );
};

export default HomeAnalyticsCharts;
